import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GlassCard } from '../../components/GlassCard';
import { useUserStore } from '../../store/userStore';
import { createCheckIn, getTodayCheckIn } from '../../lib/api';

const SCALES = {
  mood: {
    label: 'How are you feeling in yourself?',
    key: 'mood',
    options: [
      { v: 1, icon: 'fa-face-sad-tear', label: 'Struggling', color: '#EF4444' },
      { v: 2, icon: 'fa-face-frown', label: 'Low', color: '#F97316' },
      { v: 3, icon: 'fa-face-meh', label: 'Okay', color: '#F59E0B' },
      { v: 4, icon: 'fa-face-smile', label: 'Good', color: '#10B981' },
      { v: 5, icon: 'fa-face-grin-stars', label: 'Great', color: '#14B8A6' },
    ],
    reflect: {
      1: 'Sending you warmth — thank you for showing up today. 💜',
      2: 'Gentle days count too. Be kind to yourself.',
      3: 'A steady day is a good foundation.',
      4: 'Lovely to hear — keep doing what feels good.',
      5: 'Wonderful! Let’s savour this one. ✨',
    },
  },
  energy: {
    label: 'How’s your energy today?',
    key: 'energy',
    options: [
      { v: 1, icon: 'fa-battery-empty', label: 'Drained', color: '#EF4444' },
      { v: 2, icon: 'fa-battery-quarter', label: 'Low', color: '#F97316' },
      { v: 3, icon: 'fa-battery-half', label: 'Steady', color: '#F59E0B' },
      { v: 4, icon: 'fa-battery-three-quarters', label: 'Good', color: '#10B981' },
      { v: 5, icon: 'fa-battery-full', label: 'Buzzing', color: '#14B8A6' },
    ],
  },
  sleep_quality: {
    label: 'How did you sleep?',
    key: 'sleep_quality',
    options: [
      { v: 1, icon: 'fa-cloud-moon', label: 'Restless', color: '#EF4444' },
      { v: 2, icon: 'fa-moon', label: 'Broken', color: '#F97316' },
      { v: 3, icon: 'fa-moon', label: 'Okay', color: '#F59E0B' },
      { v: 4, icon: 'fa-bed', label: 'Restful', color: '#6366F1' },
      { v: 5, icon: 'fa-bed', label: 'Deep', color: '#14B8A6' },
    ],
  },
  stress_level: {
    label: 'How calm or stretched do you feel?',
    key: 'stress_level',
    options: [
      { v: 1, icon: 'fa-spa', label: 'Calm', color: '#14B8A6' },
      { v: 2, icon: 'fa-leaf', label: 'Relaxed', color: '#10B981' },
      { v: 3, icon: 'fa-face-meh', label: 'Manageable', color: '#F59E0B' },
      { v: 4, icon: 'fa-wind', label: 'Tense', color: '#F97316' },
      { v: 5, icon: 'fa-bolt', label: 'Overwhelmed', color: '#EF4444' },
    ],
  },
};

const SYMPTOMS = [
  { name: 'Hot flushes', icon: 'fa-fire' },
  { name: 'Night sweats', icon: 'fa-droplet' },
  { name: 'Headache', icon: 'fa-head-side-cough' },
  { name: 'Bloating', icon: 'fa-circle-dot' },
  { name: 'Anxiety', icon: 'fa-heart-pulse' },
  { name: 'Brain fog', icon: 'fa-cloud' },
  { name: 'Irritability', icon: 'fa-bolt' },
  { name: 'Fatigue', icon: 'fa-battery-quarter' },
  { name: 'Joint pain', icon: 'fa-bone' },
  { name: 'Insomnia', icon: 'fa-eye' },
];
const FLOWS = ['Spotting', 'Light', 'Medium', 'Heavy'];
const PERIOD_MOODS = ['Low mood', 'Irritable', 'Anxious', 'Normal'];

function ExpressiveScale({ scale, value, onChange, delay }) {
  const selected = scale.options.find((o) => o.v === value);
  return (
    <div className="animate-fade-up" style={{ animationDelay: delay }}>
      <div className="flex items-baseline justify-between mb-3">
        <span className="font-display text-lg">{scale.label}</span>
        {selected && (
          <span className="text-sm font-semibold" style={{ color: selected.color }}>{selected.label}</span>
        )}
      </div>
      <div className="grid grid-cols-5 gap-2">
        {scale.options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              data-testid={`track-${scale.key}-${o.v}`}
              onClick={() => onChange(o.v)}
              className="group flex flex-col items-center gap-1.5 rounded-2xl py-3 transition-base"
              style={active ? { background: `${o.color}14` } : {}}
            >
              <span
                className="grid place-items-center rounded-full transition-base"
                style={{
                  width: 46,
                  height: 46,
                  background: active ? o.color : 'var(--aura-bg)',
                  color: active ? '#fff' : 'var(--aura-ink-soft)',
                  transform: active ? 'scale(1.12)' : 'scale(1)',
                  boxShadow: active ? `0 8px 18px -6px ${o.color}` : 'none',
                }}
              >
                <i className={`fa-solid ${o.icon} text-lg`} />
              </span>
              <span className="text-[10px] font-medium" style={{ color: active ? o.color : 'var(--aura-ink-soft)' }}>
                {o.label}
              </span>
            </button>
          );
        })}
      </div>
      {scale.reflect && selected && scale.reflect[value] && (
        <div className="mt-3 text-sm rounded-xl px-3 py-2" style={{ background: `${selected.color}12`, color: selected.color }} data-testid="mood-reflection">
          {scale.reflect[value]}
        </div>
      )}
    </div>
  );
}

export default function Track() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [v, setV] = useState({ mood: 3, energy: 3, sleep_quality: 3, stress_level: 2, pain_level: 0, period_pain: 0 });
  const [symptoms, setSymptoms] = useState([]);
  const [periodStarted, setPeriodStarted] = useState(false);
  const [flow, setFlow] = useState(null);
  const [periodMood, setPeriodMood] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedToday, setSavedToday] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    getTodayCheckIn(user.id).then((c) => {
      if (c) {
        setSavedToday(true);
        setV({ mood: c.mood, energy: c.energy, sleep_quality: c.sleep_quality, stress_level: c.stress_level, pain_level: c.pain_level || 0, period_pain: c.period_pain || 0 });
        setSymptoms(c.symptoms || []);
        setPeriodStarted(c.period_started || false);
        setFlow(c.period_flow || null);
        setPeriodMood(c.period_mood || []);
        setNotes(c.notes || '');
      }
    }).catch(() => {});
  }, [user?.id]);

  const setKey = (key) => (val) => setV((p) => ({ ...p, [key]: val }));
  const toggle = (arr, setArr, item) => setArr(arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item]);

  const firstName = (user?.name || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  const save = async () => {
    setSaving(true);
    try {
      await createCheckIn({
        user_id: user.id,
        ...v,
        symptoms,
        notes,
        period_started: periodStarted,
        period_flow: periodStarted ? flow : null,
        period_mood: periodStarted ? periodMood : [],
      });
      toast.success('Thank you for checking in today 💜');
      setSavedToday(true);
      navigate('/app');
    } catch (e) {
      toast.error('Could not save check-in');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
      {/* Warm header */}
      <div className="mb-6 animate-fade-up">
        <p className="text-xs uppercase tracking-widest text-[var(--aura-ink-soft)]">{today}</p>
        <h1 className="font-display text-3xl sm:text-4xl mt-1">{greet}, {firstName}</h1>
        <p className="text-[var(--aura-ink-soft)] mt-1">
          {savedToday ? 'You’ve checked in today — feel free to update anything below.' : 'Take a gentle moment to notice how today feels. There are no wrong answers.'}
        </p>
      </div>

      {/* Feelings */}
      <GlassCard className="p-6 mb-5 space-y-7">
        <ExpressiveScale scale={SCALES.mood} value={v.mood} onChange={setKey('mood')} delay="0.02s" />
        <div className="h-px bg-[var(--aura-line)]" />
        <ExpressiveScale scale={SCALES.energy} value={v.energy} onChange={setKey('energy')} delay="0.06s" />
        <div className="h-px bg-[var(--aura-line)]" />
        <ExpressiveScale scale={SCALES.sleep_quality} value={v.sleep_quality} onChange={setKey('sleep_quality')} delay="0.1s" />
        <div className="h-px bg-[var(--aura-line)]" />
        <ExpressiveScale scale={SCALES.stress_level} value={v.stress_level} onChange={setKey('stress_level')} delay="0.14s" />
      </GlassCard>

      {/* Body / symptoms */}
      <GlassCard className="p-6 mb-5 animate-fade-up">
        <h3 className="font-display text-lg mb-1">How’s your body today?</h3>
        <p className="text-sm text-[var(--aura-ink-soft)] mb-4">Tap anything you’ve noticed — leave blank if all is well.</p>
        <div className="flex flex-wrap gap-2">
          {SYMPTOMS.map((sym) => {
            const on = symptoms.includes(sym.name);
            return (
              <button
                key={sym.name}
                data-testid={`symptom-${sym.name.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => toggle(symptoms, setSymptoms, sym.name)}
                className="flex items-center gap-2 rounded-full px-3.5 py-2 text-sm border-2 transition-base"
                style={on ? { background: 'var(--aura-purple)', borderColor: 'var(--aura-purple)', color: '#fff' } : { borderColor: 'var(--aura-line)' }}
              >
                <i className={`fa-solid ${sym.icon} text-xs ${on ? '' : 'text-[var(--aura-ink-soft)]'}`} />
                {sym.name}
              </button>
            );
          })}
        </div>

        {/* Optional discomfort scale */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-2"><i className="fa-solid fa-hand-dots text-[#EF4444]" /> Any aches or discomfort?</span>
            <span className="text-sm font-semibold text-[#EF4444]">{['None', 'A little', 'Mild', 'Moderate', 'Strong', 'Severe'][v.pain_level]}</span>
          </div>
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                data-testid={`track-pain_level-${n}`}
                onClick={() => setKey('pain_level')(n)}
                className="flex-1 h-3 rounded-full transition-base"
                style={{ background: v.pain_level >= n && n > 0 ? '#EF4444' : v.pain_level === 0 && n === 0 ? '#94A3B8' : 'var(--aura-line)' }}
                aria-label={`pain ${n}`}
              />
            ))}
          </div>
        </div>
      </GlassCard>

      {/* Cycle */}
      <GlassCard className="p-6 mb-5 animate-fade-up">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg flex items-center gap-2"><i className="fa-solid fa-droplet text-[#EC4899]" /> Cycle</h3>
            <p className="text-sm text-[var(--aura-ink-soft)]">On your period today?</p>
          </div>
          <button
            data-testid="period-toggle"
            onClick={() => setPeriodStarted((p) => !p)}
            className="relative w-12 h-7 rounded-full transition-base"
            style={{ background: periodStarted ? '#EC4899' : 'var(--aura-line)' }}
          >
            <span className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-base" style={{ left: periodStarted ? 22 : 2 }} />
          </button>
        </div>
        {periodStarted && (
          <div className="mt-4 animate-fade-up">
            <div className="text-sm text-[var(--aura-ink-soft)] mb-2">Flow</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {FLOWS.map((f) => (
                <button key={f} data-testid={`flow-${f.toLowerCase()}`} onClick={() => setFlow(f)} className="rounded-full px-3.5 py-2 text-sm border-2 transition-base"
                  style={flow === f ? { background: '#EC4899', borderColor: '#EC4899', color: '#fff' } : { borderColor: 'var(--aura-line)' }}>
                  {f}
                </button>
              ))}
            </div>
            <div className="text-sm text-[var(--aura-ink-soft)] mb-2">How it’s affecting your mood</div>
            <div className="flex flex-wrap gap-2">
              {PERIOD_MOODS.map((m) => (
                <button key={m} data-testid={`period-mood-${m.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => toggle(periodMood, setPeriodMood, m)} className="rounded-full px-3.5 py-2 text-sm border-2 transition-base"
                  style={periodMood.includes(m) ? { background: '#EC4899', borderColor: '#EC4899', color: '#fff' } : { borderColor: 'var(--aura-line)' }}>
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      {/* Notes */}
      <GlassCard className="p-6 mb-6 animate-fade-up">
        <h3 className="font-display text-lg mb-1">Anything on your mind?</h3>
        <p className="text-sm text-[var(--aura-ink-soft)] mb-3">A thought, a win, a worry — this is your space.</p>
        <textarea
          data-testid="track-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Today I felt…"
          className="w-full rounded-xl border border-[var(--aura-line)] px-4 py-3 outline-none focus:border-[var(--aura-purple)] resize-none"
        />
      </GlassCard>

      <button
        data-testid="save-checkin-btn"
        onClick={save}
        disabled={saving}
        className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 disabled:opacity-70 mb-6 text-base"
      >
        {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-heart" /> {savedToday ? 'Update today’s check-in' : 'Save today’s check-in'}</>}
      </button>
    </div>
  );
}
